import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Area, AreaChart,
} from "recharts";
import { CalendarDays, Play, Flag, TrendingUp } from "lucide-react";

/**
 * Campaign summary + two interactive charts:
 *   1. Post frequency per day (pink bar chart) — hover shows date + count.
 *   2. Views over time with a Cumulative / Daily toggle (soft pink area).
 *
 * `posts` is the same array LatestRunSummary already receives, so the
 * timeline is scoped by the active date filter (no extra API call).
 */
// Legacy rows can carry post_date as a unix int (or numeric string) —
// parse defensively so one bad row can't produce "Invalid Date"/NaN tiles.
const toDateISO = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" || /^\d{9,13}$/.test(String(v).trim())) {
    const n = Number(v);
    const ms = n > 1e12 ? n : n * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

export function CampaignTimeline({ posts = [] }) {
  const [viewsMode, setViewsMode] = useState("cumulative");
  const { first, last, days, dailyPosts, dailyViews, cumViews } = useMemo(() => {
    const dated = posts
      .map((p) => ({
        d: toDateISO(p.post_date),
        v: Number(p.video_views || p.video_plays || 0),
      }))
      .filter((p) => p.d)
      .sort((a, b) => (a.d < b.d ? -1 : 1));
    if (dated.length === 0) {
      return { first: null, last: null, days: 0, dailyPosts: [], dailyViews: [], cumViews: [] };
    }
    const first = dated[0].d;
    const last = dated[dated.length - 1].d;
    const start = new Date(first + "T00:00:00Z");
    const end = new Date(last + "T00:00:00Z");
    const days = Math.max(1, Math.round((end - start) / 86400000) + 1);

    const bucket = new Map();
    for (const p of dated) {
      const b = bucket.get(p.d) || { d: p.d, count: 0, views: 0 };
      b.count += 1;
      b.views += p.v;
      bucket.set(p.d, b);
    }
    // Fill missing days so the timeline reads continuously
    const dailyPosts = [];
    const dailyViews = [];
    let running = 0;
    for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
      const iso = new Date(t).toISOString().slice(0, 10);
      const b = bucket.get(iso) || { d: iso, count: 0, views: 0 };
      dailyPosts.push({ date: iso, count: b.count });
      running += b.views;
      dailyViews.push({ date: iso, daily: b.views, cumulative: running });
    }
    return { first, last, days, dailyPosts, dailyViews, cumViews: dailyViews };
  }, [posts]);

  if (!first) {
    return (
      <div className="el-card p-10 text-center text-xs font-mono text-slate-400"
           data-testid="campaign-timeline-empty">
        No dated posts in the current filter — timeline will populate after the first fetch.
      </div>
    );
  }

  const totalViews = dailyViews.length ? dailyViews[dailyViews.length - 1].cumulative : 0;
  const compact = (n) => {
    if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
    return String(n);
  };
  const fmtDate = (iso) => {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  };

  return (
    <div className="space-y-4" data-testid="campaign-timeline-block">
      {/* Meta strip: first / last / duration / total views */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetaTile icon={Play}  label="Campaign started" value={fmtDate(first)} testId="campaign-first-date" />
        <MetaTile icon={Flag}  label="Latest post"       value={fmtDate(last)}  testId="campaign-last-date" />
        <MetaTile icon={CalendarDays} label="Duration"   value={`${days} day${days === 1 ? "" : "s"}`} testId="campaign-duration" />
        <MetaTile icon={TrendingUp} label="Total views"  value={compact(totalViews)} testId="campaign-total-views" />
      </div>

      {/* Post frequency */}
      <div className="el-card p-4" data-testid="chart-post-frequency">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 tracking-tight">Campaign Timeline · Frequency</h4>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">
              Posts published per day
            </p>
          </div>
          <span className="text-[10px] font-mono text-slate-400 tabular-nums">{dailyPosts.length} days</span>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyPosts} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d).replace(/, \d{2}$/, "")}
                     tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono, monospace" }}
                     axisLine={{ stroke: "#E2E8F0" }} tickLine={false} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono, monospace" }}
                     axisLine={false} tickLine={false} allowDecimals={false} width={30} />
              <Tooltip content={<CountTip />} cursor={{ fill: "rgba(79, 70, 229, 0.06)" }} />
              <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Views over time */}
      <div className="el-card p-4" data-testid="chart-views-over-time">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 tracking-tight">Views</h4>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">
              {viewsMode === "cumulative" ? "Cumulative Reel/Video views" : "Daily Reel/Video views"}
            </p>
          </div>
          <div className="flex items-center gap-1 p-0.5 bg-slate-100 border border-slate-200 rounded-lg"
               data-testid="views-mode-toggle">
            {[{ id: "cumulative", label: "Cumulative" }, { id: "daily", label: "Daily" }].map((m) => (
              <button
                key={m.id}
                onClick={() => setViewsMode(m.id)}
                data-testid={`views-mode-${m.id}`}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  viewsMode === m.id
                    ? "bg-white text-slate-900 shadow-sm border border-slate-200/80"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyViews} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#4F46E5" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d).replace(/, \d{2}$/, "")}
                     tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono, monospace" }}
                     axisLine={{ stroke: "#E2E8F0" }} tickLine={false} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono, monospace" }}
                     axisLine={false} tickLine={false} tickFormatter={compact} width={40} />
              <Tooltip content={<ViewsTip mode={viewsMode} />} cursor={{ stroke: "#4F46E5", strokeOpacity: 0.3 }} />
              <Area type="monotone" dataKey={viewsMode === "cumulative" ? "cumulative" : "daily"}
                    stroke="#4F46E5" strokeWidth={2.5} fill="url(#viewsFill)" dot={false} activeDot={{ r: 4, fill: "#4F46E5", stroke: "#EEF2FF", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const MetaTile = ({ icon: Icon, label, value, testId }) => (
  <div className="el-tile px-4 py-3" data-testid={testId}>
    <div className="flex items-center justify-between text-slate-400 mb-1">
      <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
      <Icon size={12} className="text-pink-500 opacity-80" />
    </div>
    <div className="text-base font-semibold tracking-tight text-slate-900 tabular-nums">{value}</div>
  </div>
);

const CountTip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg bg-white border border-slate-200 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900">
        {new Date(label + "T00:00:00Z").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
      </div>
      <div className="text-slate-500 text-[10px] font-mono uppercase tracking-wider mt-0.5">Posts published</div>
      <div className="mt-1 text-slate-900 tabular-nums font-medium">{p.count}</div>
    </div>
  );
};

const ViewsTip = ({ active, payload, label, mode }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const val = mode === "cumulative" ? p.cumulative : p.daily;
  return (
    <div className="rounded-lg bg-white border border-slate-200 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900">
        {new Date(label + "T00:00:00Z").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
      </div>
      <div className="text-slate-500 text-[10px] font-mono uppercase tracking-wider mt-0.5">
        {mode === "cumulative" ? "Total views" : "Daily views"}
      </div>
      <div className="mt-1 text-slate-900 tabular-nums font-medium">{val.toLocaleString()}</div>
    </div>
  );
};
